import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/components/language-provider";

export const PAGE_SIZES = [25, 50, 100] as const;

/** Page size remembered per list (localStorage can be unavailable: falls back to 25). */
export function storedPageSize(key: string): number {
  try {
    const v = Number(localStorage.getItem(key));
    return (PAGE_SIZES as readonly number[]).includes(v) ? v : 25;
  } catch {
    return 25;
  }
}

export function rememberPageSize(key: string, size: number) {
  try { localStorage.setItem(key, String(size)); } catch { /* private mode */ }
}

/** "1–25 of 1.234", previous/next and rows per page. The lists page in the API (up to 100 rows). */
export function ListPager({ page, pageSize, total, onPage, onPageSize, testId = "pager" }: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
  testId?: string;
}) {
  const { t, language } = useLanguage();
  const root = useRef<HTMLDivElement>(null);
  const nf = (n: number) => n.toLocaleString(language === "pt" ? "pt-BR" : "en-US");
  // A new page starts at the top of the list; the app scrolls inside <main>, not the window.
  const go = (p: number) => {
    onPage(p);
    const main = root.current?.closest("main");
    if (main && main.scrollHeight > main.clientHeight) main.scrollTo({ top: 0, behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  if (total <= PAGE_SIZES[0] && page === 1) return null;
  return (
    <div ref={root} className="mt-4 flex flex-col-reverse items-center justify-between gap-3 text-sm text-muted-foreground sm:flex-row" data-testid={testId}>
      <label className="flex items-center gap-2">
        <select
          className="h-8 rounded-md border bg-background px-2 text-sm text-foreground"
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          data-testid={`${testId}-size`}
        >
          {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {t("docs.perPage")}
      </label>
      <div className="flex items-center gap-2">
        <span data-testid={`${testId}-range`}>{t("docs.pageRange").replace("{from}", nf(from)).replace("{to}", nf(to)).replace("{total}", nf(total))}</span>
        <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => go(page - 1)} aria-label={t("docs.prevPage")} data-testid={`${testId}-prev`}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= pages} onClick={() => go(page + 1)} aria-label={t("docs.nextPage")} data-testid={`${testId}-next`}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/** Fetches one page of a list endpoint with its total (X-Total-Count; the body stays a plain array). */
export async function fetchPage<T>(url: string, headers: Record<string, string>): Promise<{ items: T[]; total: number }> {
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  const items: T[] = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
  const header = Number(res.headers.get("X-Total-Count"));
  return { items, total: Number.isFinite(header) && res.headers.has("X-Total-Count") ? header : items.length };
}
